import { useParams } from 'react-router';
import { isNullish } from 'remeda';
import { useLoadTemplateV2 } from 'shared/hooks/use-load-template-v2';
import { isBodyBlank } from './body-content';
import { CHANNEL_CONFIGS, type TemplateChannel } from './channels';
import { draftKey, useSendingOptionsDraftsAtom, useTemplateDraftsAtom } from './state';

// `locale` is whatever the body carries: the v2 template schema types it as a plain
// string, so a body in a language this build does not know still reports itself.
export type BlankField = { channel: TemplateChannel; locale: string; field: 'body' | 'subject' };

// Only fields edited in this session are considered: one already stored blank would
// otherwise lock the whole template, including edits to the other languages.
export const useBlankFields = (): BlankField[] => {
  const { templateId } = useParams<{ templateId: string }>();
  const { template } = useLoadTemplateV2(templateId);
  const [drafts] = useTemplateDraftsAtom();
  const [sendingOptionsDrafts] = useSendingOptionsDraftsAtom();

  if (!template) return [];

  return CHANNEL_CONFIGS.flatMap(({ key: channel }) =>
    template.bodies[channel].flatMap((body): BlankField[] => {
      const key = draftKey(template.id, channel, body.locale);
      const bodyDraft = drafts[key];
      const subjectDraft = sendingOptionsDrafts[key]?.subject;

      return [
        ...(!isNullish(bodyDraft) && isBodyBlank(bodyDraft)
          ? [{ channel, locale: body.locale, field: 'body' as const }]
          : []),
        // Only emails carry a subject, and only there does the backend require one.
        ...(channel === 'email' && !isNullish(subjectDraft) && subjectDraft.trim() === ''
          ? [{ channel, locale: body.locale, field: 'subject' as const }]
          : []),
      ];
    }),
  );
};
